import React, { useState, useEffect } from 'react';
import ComponentSpecRenderer from './ComponentSpecRenderer';

import { fetchCategoryComponentDefinitions, fetchColorwaySpecifications } from '../../../../../utils/api-service/get-services';
import { updateColorwaySpecifications } from '../../../../../utils/api-service/put-services';

const ColorwaySpecifications = ({ productId, colorwayId, colorwayName, onSave, productType }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [components, setComponents] = useState([]);
    const [specifications, setSpecifications] = useState({
        components: [],
        notes: ''
    });

    useEffect(() => {
        if (productType)
            loadData();
    }, [productType, colorwayId]);

    const loadData = async () => {
        setLoading(true);
        try {
            // Load component definitions from category
            const compRes = await fetchCategoryComponentDefinitions(productType);
            if (compRes.success) {
                setComponents(compRes.components);
            }

            // Load existing specifications for this colorway
            const specsRes = await fetchColorwaySpecifications(productId, colorwayId);
            if (specsRes.success && specsRes.specifications) {
                setSpecifications(specsRes.specifications);
            }
        } catch (error) {
            console.error('Error loading specifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleComponentChange = (componentName, items) => {
        setSpecifications(prev => {
            const updatedComponents = prev.components.filter(c => c.componentName !== componentName);
            if (items && items.length > 0) {
                updatedComponents.push({ componentName, items });
            }
            return { ...prev, components: updatedComponents };
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await updateColorwaySpecifications(productId, colorwayId, specifications);
            if (response.success) {
                if (onSave) onSave(response.specifications);
                alert('Specifications saved successfully!');
            } else {
                alert(response.message || 'Failed to save');
            }
        } catch (error) {
            console.error('Error saving:', error);
            alert('Error saving specifications');
        } finally {
            setSaving(false);
        }
    };

    const getComponentItems = (componentName) => {
        const component = specifications.components.find(c => c.componentName === componentName);
        return component?.items || [];
    };

    if (loading) {
        return <div className="text-center py-5">Loading specifications...</div>;
    }

    return (
        <div className="colorway-specifications">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0">Technical Specifications: {colorwayName}</h5>
                <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save Specifications'}
                </button>
            </div>

            {/* Dynamic Components */}
            {components.map(component => (
                <ComponentSpecRenderer
                    key={component.name}
                    componentDef={component}
                    items={getComponentItems(component.name)}
                    onItemsChange={(items) => handleComponentChange(component.name, items)}
                />
            ))}

            {/* Notes Section */}
            <div className="card mt-4">
                <div className="card-body">
                    <label className="form-label fw-bold">Additional Notes</label>
                    <textarea
                        className="form-control"
                        rows="3"
                        value={specifications.notes || ''}
                        onChange={(e) => setSpecifications(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Add any colorway-specific notes or special instructions..."
                    />
                </div>
            </div>
        </div>
    );
};

export default ColorwaySpecifications;